import { Badge } from "@components/ui/badge";
import { cn } from "@lib/utils";
import type { Artifact, KeywordRow, KeywordTier, TableArtifactData } from "@shared";
import type { ArtifactPresentation, RenderCell, RenderSimpleRow } from "@components/artifacts";
import { toSimpleRow } from "./keywordOpportunity";

// How a keyword table presents itself — the one domain edge over the generic
// ReviewableTable. Lives apart from the stream-coupled mount so Storybook (and
// anything else) can render the real thing with mock data.

// Intent badge tones, matching the design: Local reads blue, the buyer-intent
// tiers lavender/violet, informational stays muted.
const INTENT_BADGE: Record<string, string> = {
  local: "bg-sky-50 text-sky-700 border-sky-200",
  commercial: "bg-indigo-50 text-indigo-700 border-indigo-200",
  transactional: "bg-violet-50 text-violet-700 border-violet-200",
  informational: "bg-neutral-100 text-neutral-500 border-neutral-200",
  navigational: "bg-neutral-100 text-neutral-500 border-neutral-200",
  default: "bg-neutral-100 text-neutral-500 border-neutral-200",
};

const compact = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

/**
 * Expert-view cell formatting: Launch10 Score as a number, volume compact (110k),
 * CPC as currency ($6.20), competition/intent as badges. Unknown columns
 * stringify so the table degrades gracefully.
 */
export const keywordCell: RenderCell = (column, row) => {
  const value = (row as Record<string, unknown>)[column.key];
  switch (column.key) {
    case "launch10Score":
      return typeof value === "number" ? (
        <span className="font-semibold text-base-600">{Math.round(value)}</span>
      ) : (
        String(value ?? "")
      );
    case "searchVolume":
      return typeof value === "number" ? compact.format(value) : String(value ?? "");
    case "cpc":
      return typeof value === "number" ? currency.format(value) : value == null ? "—" : String(value);
    case "intent": {
      if (!value) return null;
      const tone = INTENT_BADGE[String(value).toLowerCase()] ?? INTENT_BADGE.default;
      return (
        <Badge variant="outline" className={cn("capitalize font-normal", tone)}>
          {String(value)}
        </Badge>
      );
    }
    case "competitionLevel":
      return value ? (
        <Badge variant="secondary" className="capitalize font-normal">
          {String(value)}
        </Badge>
      ) : null;
    default:
      return value == null ? "" : String(value);
  }
};

const TIER_BADGE: Record<KeywordTier, string> = {
  top_pick: "bg-emerald-50 text-emerald-600 border-emerald-300",
  solid: "bg-blue-50 text-blue-600 border-blue-300",
  niche: "bg-neutral-100 text-neutral-600 border-neutral-300",
};
const TIER_LABEL: Record<KeywordTier, string> = {
  top_pick: "Top pick",
  solid: "Solid",
  niche: "Fair",
};

/** Simple-view layout for a keyword row: tier pill + plain commentary + volume. */
export const renderSimpleKeywordRow: RenderSimpleRow = (row) => {
  const s = toSimpleRow(row as KeywordRow);
  const commentary = [s.volumePhrase, s.competitionPhrase].filter(Boolean).join(" · ");
  return {
    tier: (
      <span
        className={cn(
          "inline-flex w-full justify-center rounded-lg border px-2 @xl:px-3 py-1 text-xs font-semibold",
          TIER_BADGE[s.tier]
        )}
      >
        {TIER_LABEL[s.tier]}
      </span>
    ),
    title: s.title,
    subtitle: commentary,
    metric: (
      <div className="text-right tabular-nums">
        <div className="font-semibold text-neutral-900">{compact.format(s.searchVolume)}</div>
        {/* In a narrow card the keyword gets the width; the unit compresses. */}
        <div className="text-xs text-neutral-400">
          <span className="@2xl:hidden">/mo</span>
          <span className="hidden @2xl:inline">searches/mo</span>
        </div>
      </div>
    ),
  };
};

/** The full presentation config a keyword artifact hands to ArtifactRenderer. */
export function keywordPresent(artifact: Artifact): ArtifactPresentation {
  const data = artifact.data as TableArtifactData;
  return {
    title: "Keyword opportunities",
    subtitle: `${data.rows.length} keywords`,
    location: data.location,
    renderCell: keywordCell,
    renderSimpleRow: renderSimpleKeywordRow,
    sortedLabel: "Sorted by best opportunity",
    // Short headers, frontend-owned: keeps the Expert table inside the card (so
    // the accept/reject buttons stay visible) and relabels regardless of what
    // labels were persisted on the artifact's columns.
    columnLabels: {
      keyword: "Keyword",
      launch10Score: "Score",
      searchVolume: "Vol",
      cpc: "CPC",
      competitionLevel: "Comp",
      intent: "Intent",
    },
  };
}
